import { useToast, useDisclosure } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";

import CategoryForm from "./CategoryForm";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { UseOfSoldProductsModalWrapper } from "../CategoryModalWrappers";
import * as React from "react";
import { useCallback, useState } from "react";
import {
  organizationQuestionnaireKeys
} from "../../../../../../hooks/query";
import {
  useOfSoldProductsKeys,
  useGetUseOfSoldProductsItems,
  useAddUseOfSoldProductsItems,
  useUpdateUseOfSoldProductsItem,
  useDeleteUseOfSoldProductsItem,
  useUpdateUseOfSoldProducts,
  useImportUseOfSoldProducts,
  useDownloadUseOfSoldProductsTemplate
} from "../../../../../../hooks/query/questionnaire/categories/useOfSoldProducts";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { buildDimensionFormDefaults, withDimensionItems } from "./helpers";

function CategoryUseOfSoldProducts({
                                     defaultValues,
                                     handleCategoryChange,
                                     tabKey,
                                     finalize,
                                     finalizeQuestionnaire,
                                     isPreview,
                                     organizationStructure,
                                     dimensionItems
                                   }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { id: questionnaireId } = defaultValues;

  const FORM_INPUT_KEY = "useOfSoldProducts";
  const colHeaders = [
    t(`inputs.${FORM_INPUT_KEY}.brand`),
    t(`inputs.${FORM_INPUT_KEY}.itemNumber`),
    t(`inputs.${FORM_INPUT_KEY}.itemDescription`),
    t(`inputs.${FORM_INPUT_KEY}.gasConsumptionItem`),
    t(`inputs.${FORM_INPUT_KEY}.electricityConsumptionItem`),
    t(`inputs.${FORM_INPUT_KEY}.averageExpectedLifeSpan`),
    t(`inputs.${FORM_INPUT_KEY}.numberOfItemSold`)
  ];
  const attributesOrder = [
    "brand",
    "itemNumber",
    "itemDescription",
    "gasConsumptionItem",
    "electricityConsumptionItem",
    "averageExpectedLifeSpan",
    "numberOfItemSold"
  ];

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const {
    handleSubmit,
    formState: { isDirty }
  } = useForm();

  const formId = `formStep${tabKey}`;

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Use of Sold Products saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateUseOfSoldProducts(queryConfig);

  const onSubmit = (() => {
    if (!isPreview) {
      if (isDirty) {
        update({ questionnaireId, data: {} });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  const prepareSectionValues = useCallback(items => {
    return items.map(item => ({
      id: item.id ?? null,
      dimensionItems: item.dimensionItems ?? null,
      dimensionOneItem: item.dimensionItems?.dimensionOneItem ?? null,
      dimensionTwoItem: item.dimensionItems?.dimensionTwoItem ?? null,
      dimensionThreeItem: item.dimensionItems?.dimensionThreeItem ?? null,
      dimensionFourItem: item.dimensionItems?.dimensionFourItem ?? null,
      dimensionFiveItem: item.dimensionItems?.dimensionFiveItem ?? null,
      brand: item.brand ?? "",
      itemNumber: item.itemNumber ?? "",
      itemDescription: item.itemDescription ?? "",
      gasConsumptionItem: item.gasConsumptionItem ?? "",
      electricityConsumptionItem: item.electricityConsumptionItem ?? "",
      averageExpectedLifeSpan: item.averageExpectedLifeSpan ?? "",
      numberOfItemSold: item.numberOfItemSold ?? ""
    }));
  }, []);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        dimensionItems: item.dimensionItems ?? null,
        brand: item.brand ?? "",
        itemNumber: item.itemNumber ?? "",
        itemDescription: item.itemDescription ?? "",
        gasConsumptionItem: item.gasConsumptionItem ?? "",
        electricityConsumptionItem: item.electricityConsumptionItem ?? "",
        averageExpectedLifeSpan: item.averageExpectedLifeSpan ?? "",
        numberOfItemSold: item.numberOfItemSold ?? ""
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddUseOfSoldProductsItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(useOfSoldProductsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateUseOfSoldProductsItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(useOfSoldProductsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteUseOfSoldProductsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(useOfSoldProductsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: downloadTemplate } = useDownloadUseOfSoldProductsTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsed = err.response?.data
        ? JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)))
        : null;
      showErrorToast(toast, parsed?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: importItems } = useImportUseOfSoldProducts({
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.imported"));
      queryClient.invalidateQueries(useOfSoldProductsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleImport = (e) => {
    if (!e.target.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    dispatch(showAppSpinner());
    importItems({ questionnaireId, data: formData });
  };

  const handleCategoryModalSubmit = (formData) => {
    const apiData = formData[categoryModalData.fieldArrayName].map(el => withDimensionItems({ ...el }));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  const getEmptyRow = () => {
    return {
      brand: "",
      itemNumber: "",
      itemDescription: "",
      gasConsumptionItem: "",
      electricityConsumptionItem: "",
      averageExpectedLifeSpan: "",
      numberOfItemSold: "",
      ...buildDimensionFormDefaults(organizationStructure, dimensionItems)
    };
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetUseOfSoldProductsItems}
        isPreview={isPreview}
        organizationStructure={organizationStructure}
        onDownloadTemplate={() => downloadTemplate({ questionnaireId })}
        onImport={handleImport}
        importBtnId={`import-${tabKey}-btn`}
      />

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isCategoryModalOpen && categoryModalData !== null && (
          <UseOfSoldProductsModalWrapper
            tabKey={tabKey}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            getEmptyRow={getEmptyRow}
          />
        )
      }
    </CategoryForm>
  );
}

export default CategoryUseOfSoldProducts;
